import { useState, useCallback, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useNavigate, Link } from 'react-router-dom';
import { Check, Loader2, User, LogOut } from 'lucide-react';
import { API_BASE } from '@/lib/api';
import type { SwitchableUser } from '@/lib/types';

interface SwitchUserMenuProps {
  children: (props: { toggle: () => void; open: boolean }) => ReactNode;
  showExtraItems?: boolean;
  side?: 'top' | 'bottom';
}

// TEMPORARY TEST-ONLY FEATURE.
// Must remain disabled in production and should be removed before launch.
export function SwitchUserMenu({ children, showExtraItems = true, side = 'top' }: SwitchUserMenuProps) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<SwitchableUser[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const { switchUser, logout } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const isFeatureEnabled = import.meta.env.VITE_ENABLE_TEST_USER_SWITCH === 'true';

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setLoadingUsers(true);
      fetchUsers().finally(() => setLoadingUsers(false));
    }
  };

  async function fetchUsers() {
    try {
      const token = sessionStorage.getItem('access_token');
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API_BASE}/auth/test-users`, { headers, credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success) {
        setUsers(data.data || []);
        setCurrentUserId(data.currentUserId);
      }
    } catch {
      /* silent */
    }
  }

  async function handleSwitch(targetUserId: string) {
    if (targetUserId === currentUserId) {
      close();
      return;
    }
    setSwitchingTo(targetUserId);
    try {
      await switchUser(targetUserId);
      const target = users.find(u => u.id === targetUserId);
      addToast(`Switched to ${target?.name || targetUserId}`, 'success');
      close();
      navigate('/dashboard');
    } catch (err: any) {
      addToast(err.message || 'Switch failed', 'error');
    } finally {
      setSwitchingTo(null);
    }
  }

  if (!isFeatureEnabled) {
    return <>{children({ toggle: () => {}, open: false })}</>;
  }

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        {children({ toggle, open })}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side={side}
          align="start"
          sideOffset={8}
          className="z-50 w-72 rounded-lg border border-default bg-surface shadow-lg max-h-[80vh] overflow-y-auto focus:outline-none"
          aria-label="Switch test user"
        >
          <div className="p-3 border-b border-default">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-primary">Switch User</p>
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-medium">
                Testing only
              </span>
            </div>
          </div>

          <div className="p-1">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={20} className="animate-spin text-secondary" />
              </div>
            ) : (
              users.map(u => {
                const isCurrent = u.id === currentUserId;
                const isLoading = switchingTo === u.id;
                return (
                  <button
                    key={u.id}
                    onClick={() => handleSwitch(u.id)}
                    disabled={switchingTo !== null}
                    className={`flex items-center gap-3 w-full rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                      isCurrent
                        ? 'bg-brand-primary-soft text-primary cursor-default'
                        : 'text-primary hover:bg-surface-hover cursor-pointer'
                    } ${switchingTo !== null && !isCurrent ? 'opacity-50' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                      isCurrent ? 'bg-brand-500 text-white' : 'bg-surface-hover text-secondary'
                    }`}>
                      {isLoading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        u.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium truncate">{u.name}</p>
                        {isCurrent && <Check size={14} className="text-brand-500 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-secondary truncate">{u.uniqueId || u.email}</p>
                      <p className="text-[11px] text-secondary mt-0.5">{u.roles.join(', ')}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {showExtraItems && (
            <>
              <div className="border-t border-default" />
              <div className="p-1">
                <Link
                  to="/profile"
                  onClick={close}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-primary hover:bg-surface-hover"
                >
                  <User size={16} />
                  Profile
                </Link>
                <button
                  onClick={() => { close(); logout(); }}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-error hover:bg-error/10 w-full"
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            </>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
